import { FC, useCallback, useRef, useState } from "react";

import { Box, Flex, useOutsideClick } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";

import { Button, IconButton } from "@components/buttons";
import Icon from "@components/Icon";
import { LockerInput, TLockerEntry } from "@modules/locker-search";
import { LockersMap } from "@modules/lockers-map";

import { OrderFields } from "../../constants";
import useOrderFormik from "../../hooks/useOrderFormik";

type TLockerSelectMapModalProps = {
  onClose: () => void;
};

const LockerSelectMapModal: FC<TLockerSelectMapModalProps> = ({ onClose }) => {
  const { t } = useTranslation();

  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const searchNodeRef = useRef<HTMLDivElement>(null);

  useOutsideClick({
    ref: searchNodeRef,
    handler: () => setIsSearchVisible(false),
  });

  const {
    values: {
      [OrderFields.STARTING_COUNTRY]: startingCountry,
      [OrderFields.RETURN_LOCKER]: selectedLockerId,
      [OrderFields.RETURN_LOCKER_NAME]: selectedLockerName,
    },
    setValues,
  } = useOrderFormik();

  const handleLockerSelected = useCallback(
    (locker) => {
      setValues((oldValues) => ({
        ...oldValues,
        [OrderFields.RETURN_LOCKER]: locker.id,
        [OrderFields.RETURN_LOCKER_NAME]: locker.name,
      }));
    },
    [setValues]
  );

  const handleSearchLockerSelected = useCallback(
    (locker?: TLockerEntry) => {
      if (locker) {
        setValues((oldValues) => ({
          ...oldValues,
          [OrderFields.RETURN_LOCKER]: locker.id,
          [OrderFields.RETURN_LOCKER_NAME]: locker.name,
        }));
      }
      setIsSearchVisible(false);
    },
    [setValues]
  );

  return (
    <Flex
      direction="column"
      position="fixed"
      minWidth="21.875rem"
      top="3.625rem"
      bottom="0"
      left="0"
      right="0"
      bg="white"
      zIndex="100"
    >
      <Flex bg="dpd-black" color="white" height="3.625rem" align="center" justify="space-between" px="0.75rem">
        <Button
          label={t("order-locker_map-back")}
          variant="transparent"
          leftIcon={<Icon icon="chevronLeft" size="1rem" color="white" />}
          iconSpacing="0.25rem"
          fontWeight="normal"
          size="md"
          sx={{ color: "white", px: "0", py: "0", border: "none", justifyContent: "flex-start" }}
          onClick={onClose}
        />
        <IconButton
          variant="transparent"
          icon={<Icon icon="magnifier" size="1.5rem" />}
          sx={{ color: "white" }}
          onClick={() => setIsSearchVisible((isVisible) => !isVisible)}
        />
      </Flex>
      <Flex direction="column" flex="1" position="relative">
        {selectedLockerId && (
          <Box
            bg="grey.light"
            px="0.75rem"
            py="0.5rem"
            fontSize="0.875rem"
            boxShadow="dropdown"
            zIndex="10"
            position="relative"
          >
            <Box fontWeight="300">{selectedLockerName}</Box>
            <Box color="red.lead">{t("order-locker_map-selected_as_locker_in_case_of_return")}</Box>
          </Box>
        )}
        <Box flex="1">
          <LockersMap
            countryCode={startingCountry!}
            selectedLockerId={selectedLockerId || undefined}
            onLockerSelect={handleLockerSelected}
            hasLockerPopups
            hasUserPosition
            hasPositionInit
          />
        </Box>
        {isSearchVisible && (
          <Box position="absolute" backgroundColor="overlay-black" inset="0" padding="0.75rem" zIndex="10">
            <Box ref={searchNodeRef}>
              <LockerInput
                id="map-locker-search"
                countryCode={startingCountry!}
                label={t("order-locker_map-choose_or_search_locker")}
                onSelect={handleSearchLockerSelected}
              />
            </Box>
          </Box>
        )}
      </Flex>
    </Flex>
  );
};
export default LockerSelectMapModal;
